import { FC } from 'react';
import {
  Box,
  Button,
  Card,
  Collapse,
  Divider,
  Group,
  Stack,
  Text,
  Tooltip,
} from '@mantine/core';
import { IDENTIFIERS } from '@consts/systems.consts';
import classes from './PatientDemographics.module.css';
import { Patient } from '@medplum/fhirtypes';
import { calculateAge } from '@utils/calculate-age';
import { formatPhoneNumberIntl } from 'react-phone-number-input';
import ChoremeioCreatePatient from '../../ChoremeioCreatePatient';
import {
  IconCake,
  IconId,
  IconMail,
  IconMapPin,
  IconPencil,
  IconPhone,
  TablerIcon,
  IconCookieMan,
  IconCaretDown,
  IconCaretUp,
} from '@tabler/icons-react';
import { useRelatedPeople } from '@hooks/useRelatedPeople';
import { formatHumanName } from '@medplum/core';
import { useDisclosure } from '@mantine/hooks';
import { useTranslation } from 'react-i18next';

// -----------------------------------------------------------------------------
type ItemProps = {
  value: string | undefined;
  icon: TablerIcon;
};

// -----------------------------------------------------------------------------
const Item: FC<ItemProps> = ({ value, icon: Icon }) => {
  if (!value) {
    return (
      <Box className={classes.itemWrapper}>
        <Icon className={classes.itemIcon} />
        <Text size="sm">-</Text>
      </Box>
    );
  }

  return (
    <Box className={classes.itemWrapper}>
      <Icon className={classes.itemIcon} />
      <Text size="sm">{value}</Text>
    </Box>
  );
};

// -----------------------------------------------------------------------------
interface PatientDemographicsProps {
  patient: Patient;
  onPatientUpdate?: (updatedPatient: Patient) => void;
  relatedPeopleRefreshKey?: number;
}

// -----------------------------------------------------------------------------
export const PatientDemographics: FC<PatientDemographicsProps> = ({
  patient,
  onPatientUpdate,
  relatedPeopleRefreshKey,
}) => {
  const { t } = useTranslation('patient-pages');
  const [relatedPeopleOpened, { toggle: toggleRelatedPeople }] =
    useDisclosure(false);

  const relationshipMap: Record<string, string> = {
    FTH: t('summaryPage.summary.relatedPeople.relationships.father'),
    MTH: t('summaryPage.summary.relatedPeople.relationships.mother'),
    SPS: t('summaryPage.summary.relatedPeople.relationships.spouse'),
  };

  if (!patient) {
    return null;
  }

  const relatedPeople = useRelatedPeople({
    patientId: patient.id || '',
    refreshKey: relatedPeopleRefreshKey,
  });

  const splitLastName = patient?.name?.[0]?.family?.split(' ') || ['', ''];
  const birthdate = patient?.birthDate || ' ';
  const [year, month, day] = birthdate.split('-');
  const formattedDate = `${day}/${month}/${year}`;

  return (
    <Card shadow="sm" padding="lg" radius="md" withBorder>
      <Group justify="space-between">
        <Box>
          <Text fw={500} size="lg">
            {patient?.name?.[0].given} {splitLastName[0]}
          </Text>
        </Box>

        <ChoremeioCreatePatient
          patient={patient}
          existingRelatedPeople={relatedPeople}
          onUpdatePatient={onPatientUpdate || (() => {})}
          onUpdateRelatedPerson={onPatientUpdate || (() => {})}
        >
          <Tooltip
            label={t('buttons.edit', {
              ns: 'common',
              defaultValue: 'Edit',
            })}
          >
            <Button variant="light" size="xs">
              <IconPencil size={14} />
            </Button>
          </Tooltip>
        </ChoremeioCreatePatient>
      </Group>

      <Item
        value={
          patient.identifier?.find((t) => t.system === IDENTIFIERS.AMKA)?.value
        }
        icon={IconId}
      />

      <Item
        value={
          patient?.birthDate
            ? `${formattedDate} (${calculateAge(patient?.birthDate)})`
            : patient?.birthDate
        }
        icon={IconCake}
      />

      <Item value={patient?.address?.[0]?.city} icon={IconMapPin} />

      <Item
        value={formatPhoneNumberIntl(
          patient?.telecom?.find((t) => t.system === 'phone')?.value || ''
        )}
        icon={IconPhone}
      />

      <Item
        value={patient.telecom?.find((t) => t.system === 'email')?.value}
        icon={IconMail}
      />

      {relatedPeople.length > 0 && (
        <Stack gap={0}>
          <Group
            justify="space-between"
            onClick={toggleRelatedPeople}
            className={classes.collapsibleGroup}
          >
            <Text fw={500} size="sm" c="dimmed">
              {relatedPeople.length}{' '}
              {t('summaryPage.summary.relatedPeople.title')}
            </Text>

            <Button variant="transparent" p={0}>
              {relatedPeopleOpened ? (
                <IconCaretUp stroke={1.5} />
              ) : (
                <IconCaretDown stroke={1.5} />
              )}
            </Button>
          </Group>
          <Collapse in={relatedPeopleOpened}>
            {relatedPeople.map((relatedPerson, _index) => {
              const relationshipCode =
                relatedPerson.relationship?.[0]?.coding?.[0]?.code;

              const relationshipName =
                relationshipMap[relationshipCode || ''] ||
                t('summaryPage.summary.relatedPeople.relationships.unknown');

              const phone = formatPhoneNumberIntl(
                relatedPerson?.telecom?.find((t) => t.system === 'phone')
                  ?.value || ''
              );

              const email = relatedPerson.telecom?.find(
                (t) => t.system === 'email'
              )?.value;

              const amka = relatedPerson.identifier?.find(
                (t) => t.system === IDENTIFIERS.AMKA
              )?.value;

              return (
                <Stack key={relatedPerson.id} gap={0}>
                  <Item
                    value={`${formatHumanName(relatedPerson.name?.[0])} (${relationshipName})`}
                    icon={IconCookieMan}
                  />

                  {amka && <Item value={amka} icon={IconId} />}
                  {phone && <Item value={phone} icon={IconPhone} />}
                  {email && <Item value={email} icon={IconMail} />}

                  {_index !== relatedPeople.length - 1 && <Divider my="sm" />}
                </Stack>
              );
            })}
          </Collapse>
        </Stack>
      )}
    </Card>
  );
};
